import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { USER_ACCESS_SUBCARD_TITLE } from "./userAccessFormUi";
import { computePushDiagnosis } from "../../services/pushDiagnostics";
import { sendPushTest } from "../../services/users";

const formatDateTime = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString();
};

const StatusRow = ({ label, value, tone = "neutral" }) => {
  const toneClass =
    tone === "ok"
      ? "text-[#1B7A4E]"
      : tone === "warn"
        ? "text-[#B54708]"
        : tone === "ko"
          ? "text-[#AF2F3C]"
          : "text-[#222222]";

  return (
    <div className="flex items-start justify-between gap-4 py-2 border-b border-[#ececec] last:border-b-0">
      <span className="text-[13px] text-[#828899]">{label}</span>
      <span className={`text-[13px] font-medium text-right ${toneClass}`}>
        {value}
      </span>
    </div>
  );
};

const permissionTone = (permission) => {
  if (permission === "granted") return "ok";
  if (permission === "denied") return "ko";
  if (permission === "default" || permission === "unsupported") return "warn";
  return "neutral";
};

const severityTone = (severity) => {
  if (severity === "ok") return "ok";
  if (severity === "warn") return "warn";
  if (severity === "ko") return "ko";
  return "neutral";
};

/**
 * CS diagnostic panel for PWA push — actionable playbook + test push.
 */
const UserAccessPushDiagnostics = ({ data, onDiagnosticsUpdated }) => {
  const { t } = useTranslation();
  const [sendingTest, setSendingTest] = useState(false);
  const diagnostics = data?.pushDiagnostics || {};
  const tokenCount =
    typeof data?.fcmTokenCount === "number"
      ? data.fcmTokenCount
      : Array.isArray(data?.fcmTokens)
        ? data.fcmTokens.length
        : 0;
  const optIn =
    data?.receiveAnomalyPush ||
    data?.recieveCharacterizationPush ||
    data?.recieveQualicheckPush ||
    data?.receiveFillingPush;

  const diagnosis =
    data?.diagnosis ||
    computePushDiagnosis({ ...data, fcmTokenCount: tokenCount });
  const permission = diagnostics.permission || "unknown";
  const permissionLabel = t(`settingsUsers.pushPermission.${permission}`, {
    defaultValue: permission,
  });
  const subscriptionStatus =
    diagnostics.subscriptionStatus || diagnosis.code || "unknown";

  const handleSendTest = async () => {
    if (!data?._id) return;
    setSendingTest(true);
    try {
      const response = await sendPushTest(data._id);
      toast.success(
        t("settingsUsers.pushTestSuccess", {
          accepted: response.data?.accepted ?? 0,
          failed: response.data?.failed ?? 0,
        }),
      );
      onDiagnosticsUpdated?.({
        pushDiagnostics: response.data?.pushDiagnostics || data.pushDiagnostics,
        diagnosis: response.data?.diagnosis || diagnosis,
      });
    } catch (error) {
      const code = error?.response?.data?.error;
      toast.error(
        code === "no_token"
          ? t("settingsUsers.pushTestNoToken")
          : t("settingsUsers.pushTestError"),
      );
    } finally {
      setSendingTest(false);
    }
  };

  return (
    <div className="border-t border-[#d3d3d3] mt-2 mx-8 pt-4 pb-2">
      <div className="user-access-wizard-notifications">
        <h3 className={USER_ACCESS_SUBCARD_TITLE}>
          {t("settingsUsers.pushDiagnosticsTitle")}
        </h3>
        <p className="text-[13px] text-[#828899] mb-3">
          {t("settingsUsers.pushDiagnosticsHelp")}
        </p>

        <div
          className={`mb-3 rounded-md border px-4 py-3 ${
            diagnosis.severity === "ok"
              ? "border-[#b7e1c8] bg-[#f3fbf6]"
              : diagnosis.severity === "ko"
                ? "border-[#f0c4c8] bg-[#fdf5f6]"
                : "border-[#f0d7b8] bg-[#fff8f0]"
          }`}
        >
          <p
            className={`text-[13px] font-semibold mb-1 ${
              severityTone(diagnosis.severity) === "ok"
                ? "text-[#1B7A4E]"
                : severityTone(diagnosis.severity) === "ko"
                  ? "text-[#AF2F3C]"
                  : "text-[#B54708]"
            }`}
          >
            {t(`settingsUsers.pushDiagnosis.${diagnosis.code}.title`, {
              defaultValue: diagnosis.code,
            })}
          </p>
          <p className="text-[13px] text-[#5e5e5e] mb-0">
            {t(`settingsUsers.pushDiagnosis.${diagnosis.code}.action`, {
              defaultValue: "",
            })}
          </p>
        </div>

        <div className="rounded-md border border-[#e5e5e5] px-4 py-1 bg-[#fafafa]">
          <StatusRow
            label={t("settingsUsers.pushOptIn")}
            value={
              optIn ? t("settingsUsers.pushYes") : t("settingsUsers.pushNo")
            }
            tone={optIn ? "ok" : "warn"}
          />
          <StatusRow
            label={t("settingsUsers.pushSubscriptionStatus")}
            value={t(`settingsUsers.pushSubscription.${subscriptionStatus}`, {
              defaultValue: subscriptionStatus,
            })}
            tone={
              subscriptionStatus === "subscribed"
                ? "ok"
                : subscriptionStatus === "unknown"
                  ? "neutral"
                  : "warn"
            }
          />
          <StatusRow
            label={t("settingsUsers.pushBrowserPermission")}
            value={permissionLabel}
            tone={permissionTone(permission)}
          />
          <StatusRow
            label={t("settingsUsers.pushTokenCount")}
            value={String(tokenCount)}
            tone={tokenCount > 0 ? "ok" : "warn"}
          />
          <StatusRow
            label={t("settingsUsers.pushPlatform")}
            value={t(
              `settingsUsers.pushPlatformValue.${diagnostics.platform || "unknown"}`,
              { defaultValue: diagnostics.platform || "unknown" },
            )}
          />
          <StatusRow
            label={t("settingsUsers.pushStandalone")}
            value={
              diagnostics.isStandalone === true
                ? t("settingsUsers.pushYes")
                : diagnostics.isStandalone === false
                  ? t("settingsUsers.pushNo")
                  : t("settingsUsers.pushUnknown")
            }
            tone={
              diagnostics.platform === "ios" &&
              diagnostics.isStandalone === false
                ? "warn"
                : "neutral"
            }
          />
          <StatusRow
            label={t("settingsUsers.pushLastSeen")}
            value={
              formatDateTime(diagnostics.lastSeenAt) ||
              t("settingsUsers.pushNever")
            }
          />
          <StatusRow
            label={t("settingsUsers.pushLastAccepted")}
            value={
              formatDateTime(diagnostics.lastPushAcceptedAt) ||
              t("settingsUsers.pushNever")
            }
          />
          <StatusRow
            label={t("settingsUsers.pushLastDelivered")}
            value={
              formatDateTime(diagnostics.lastPushDeliveredAt) ||
              t("settingsUsers.pushNever")
            }
            tone={diagnostics.lastPushDeliveredAt ? "ok" : "neutral"}
          />
          <StatusRow
            label={t("settingsUsers.pushLastOpened")}
            value={
              formatDateTime(diagnostics.lastPushOpenedAt) ||
              t("settingsUsers.pushNever")
            }
          />
          <StatusRow
            label={t("settingsUsers.pushLastError")}
            value={diagnostics.lastPushError || t("settingsUsers.pushNone")}
            tone={diagnostics.lastPushError ? "ko" : "neutral"}
          />
        </div>

        <div className="mt-3 mb-2">
          <button
            type="button"
            className="char-button char-button-secondary"
            onClick={handleSendTest}
            disabled={sendingTest || tokenCount === 0}
          >
            {sendingTest
              ? t("settingsUsers.pushTestSending")
              : t("settingsUsers.pushTestButton")}
          </button>
          {tokenCount === 0 ? (
            <p className="text-[12px] text-[#828899] mt-2 mb-0">
              {t("settingsUsers.pushTestNoTokenHint")}
            </p>
          ) : (
            <p className="text-[12px] text-[#828899] mt-2 mb-0">
              {t("settingsUsers.pushTestHint")}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserAccessPushDiagnostics;
